/**
 * Speech recognition, via the Web Speech API.
 *
 * Two things about this API deserve to be said plainly rather than buried:
 *
 * 1. **It is not local.** In Chrome the audio is sent to Google's servers for recognition. The
 *    UI says so before the microphone is ever switched on. An app that quotes other people has
 *    no business being coy about where the user's voice goes.
 * 2. **Firefox does not implement it at all,** and Chrome only exposes it behind the `webkit`
 *    prefix. The app degrades to typing, which is a first-class input, not a consolation.
 *
 * The language has to be set *before* recognition starts and cannot change mid-utterance,
 * which is why the UI carries an explicit language switch instead of relying on detection.
 */

import { SPEECH_LOCALE } from '../core/language.js';
import type { Lang } from '../core/types.js';

/** Minimal shape of the parts of the API this app uses. */
interface RecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechResultEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechResultEventLike {
  resultIndex: number;
  results: ArrayLike<
    ArrayLike<{ transcript: string }> & { isFinal: boolean }
  >;
}

type RecognitionConstructor = new () => RecognitionLike;

function constructorFor(): RecognitionConstructor | undefined {
  const w = globalThis as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

/** True when this browser can do speech recognition at all. */
export function isSpeechSupported(): boolean {
  return constructorFor() !== undefined;
}

export class SpeechError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'SpeechError';
    this.code = code;
  }
}

/** Turns an API error code into something a person can act on. */
function describe(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission was denied. Allow it in the address bar, or type instead.';
    case 'no-speech':
      return 'Nothing was heard. Try again, a little closer to the microphone.';
    case 'audio-capture':
      return 'No microphone was found on this device.';
    case 'network':
      return 'Speech recognition needs a network connection — the audio is processed remotely.';
    case 'aborted':
      return 'Listening stopped.';
    default:
      return 'Speech recognition failed. Type instead.';
  }
}

export interface ListenHandlers {
  /** Fired repeatedly while speaking, so the UI can show words appearing. */
  onPartial?: (text: string) => void;
  /** Fired once with the final transcript. */
  onFinal: (text: string) => void;
  onError?: (error: SpeechError) => void;
  /** Fired when recognition stops, for any reason. */
  onEnd?: () => void;
}

export interface Listener {
  stop: () => void;
}

/**
 * Starts listening in the given language.
 *
 * @throws {SpeechError} immediately when the browser has no support at all
 */
export function listen(lang: Lang, handlers: ListenHandlers): Listener {
  const Ctor = constructorFor();
  if (!Ctor) {
    throw new SpeechError('unsupported', 'This browser cannot do speech recognition. Type instead.');
  }

  const recognition = new Ctor();
  recognition.lang = SPEECH_LOCALE[lang];
  recognition.interimResults = true;
  recognition.continuous = false;
  recognition.maxAlternatives = 1;

  let finalText = '';

  recognition.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (!result) continue;
      const transcript = result[0]?.transcript ?? '';
      if (result.isFinal) finalText += transcript;
      else interim += transcript;
    }
    if (interim && handlers.onPartial) handlers.onPartial((finalText + interim).trim());
  };

  recognition.onerror = (event) => {
    handlers.onError?.(new SpeechError(event.error, describe(event.error)));
  };

  recognition.onend = () => {
    const text = finalText.trim();
    if (text.length > 0) handlers.onFinal(text);
    handlers.onEnd?.();
  };

  recognition.start();
  return { stop: () => recognition.stop() };
}
